import { useState } from "react";
import type { Session } from "../lib/session";
import { useChannelKeyStore } from "../store/channelKeyStore";
import { decryptKeys } from "../lib/channel-crypto";

interface Props {
  session: Session;
  onClose: () => void;
}

export function UnlockChannelModal({ session, onClose }: Props) {
  const { set } = useChannelKeyStore();
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blob = session.encrypted_keys;
  if (!blob) {
    // Defensive: shouldn't be reachable, but if it is, close gracefully.
    onClose();
    return null;
  }

  async function unlock() {
    setError(null);
    if (!passphrase) {
      setError("Enter the passphrase the channel owner shared with you.");
      return;
    }
    setBusy(true);
    try {
      const plain = await decryptKeys(blob!, passphrase);
      set(session.id, plain);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Unlock "{session.title}"</h2>
        <div className="subtitle">
          This channel carries encrypted keys for{" "}
          <strong>{blob.providers.join(" + ")}</strong>. Enter the passphrase
          the channel owner shared with you to use them.
        </div>

        <div className="field">
          <label>Passphrase</label>
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") unlock(); }}
            autoFocus
            autoComplete="off"
          />
        </div>

        {error && <div className="share-warn">{error}</div>}

        <div className="subtitle" style={{ marginTop: 8 }}>
          Keys live in this tab only — they're gone when you close the browser.
          You'll re-enter the passphrase next time.
        </div>

        <div className="actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={unlock} disabled={busy}>
            {busy ? "Unlocking…" : "Unlock"}
          </button>
        </div>
      </div>
    </div>
  );
}
